import * as Api from '@ViewModels';
import { useState, useEffect, useCallback } from 'react';
import { numberToCurrencyStringValue, sanitizeCurrencyStringValue } from '../../../../models/UiUtils';
import { useInfiniteBoardsQuery } from '../../../../queries';
import type { ISelectBoxOption } from '../../../components/SelectBox';
import { useBaseStepState } from './useBaseStepState';
import { useStepSave, type BaseStepOverrides } from './useStepSaveFactory';

interface UseOpportunityStepProps {
	opportunityStep: Api.IOpportunityAutomationStep | null;
	template: Api.IAutomationTemplate | null;
	stepId: string;
	readonly: boolean;
	onSaveSuccess?: () => void;
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
}

interface UseOpportunityStepSaveOverrides extends BaseStepOverrides {
	newBoard?: Api.IBoard;
	newStage?: Api.IBoardStage;
	newDealSize?: string;
	newUpdateExisting?: boolean;
	newContactFilter?: Api.IContactsFilterRequest | null;
	newSendFrom?: Api.ISendFromOptions | null;
}

/**
 * Combined state + save hook for OpportunityStepRenderer.
 *
 * Owns all local state (board, stage, deal size, updateExisting)
 * plus the boards query and save action.
 */
export function useOpportunityStep({
	opportunityStep,
	template,
	stepId,
	readonly,
	onSaveSuccess,
	onSaveError,
}: UseOpportunityStepProps) {
	// Opportunity-specific state
	const [selectedBoard, setSelectedBoard] = useState<Api.IBoard | null>(opportunityStep?.boardReference || null);
	const [selectedStage, setSelectedStage] = useState<Api.IBoardStage | null>(
		opportunityStep?.boardStageReference || null
	);
	const [dealSize, setDealSize] = useState(numberToCurrencyStringValue(opportunityStep?.dealSize ?? 0));
	const [updateExisting, setUpdateExisting] = useState(opportunityStep?.updateExistingOpportunity ?? false);
	const [stageOptions, setStageOptions] = useState<ISelectBoxOption<Api.IBoardStage>[]>([]);

	// Boards query
	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		meta: {
			toastErrorMessage: 'Error loading boards',
		},
	});

	// Base state (validation, auto-save)
	const baseState = useBaseStepState({
		step: opportunityStep,
		template,
		stepId,
		readonly,
		onSaveSuccess,
		onSaveError,
	});

	// Transform boards to select options
	const boardOptions = boardsQuery.data?.pages
		.map(page => page.values)
		.flat()
		?.map(board => ({
			title: board.name,
			value: board,
		}));

	// Initialize board and stage from step data when boards load
	useEffect(() => {
		if (!boardsQuery.data || !opportunityStep?.boardReference?.id) {
			return;
		}

		const board = boardsQuery.data.pages
			.map(page => page.values)
			.flat()
			.find(b => b.id === opportunityStep.boardReference?.id);

		if (board) {
			setSelectedBoard(board);
			const stages =
				board.stages?.map(stage => ({
					title: stage.name,
					value: stage,
				})) || [];
			setStageOptions(stages);
		}
	}, [boardsQuery.data, opportunityStep?.boardReference?.id]);

	// Save logic
	const buildUpdatedStep = useCallback(
		(
			step: Api.IOpportunityAutomationStep | null,
			overrides: UseOpportunityStepSaveOverrides
		): Partial<Api.IOpportunityAutomationStep> => {
			const effectiveBoard = overrides.newBoard ?? selectedBoard;
			const effectiveStage = overrides.newStage ?? selectedStage;
			const effectiveDealSize = overrides.newDealSize ?? dealSize;
			const effectiveUpdateExisting = overrides.newUpdateExisting ?? updateExisting;
			const effectiveContactFilter =
				overrides.newContactFilter !== undefined ? overrides.newContactFilter : baseState.skipStepContactFilter;
			const effectiveSendFrom = overrides.newSendFrom !== undefined ? overrides.newSendFrom : step?.sendFromOptions;

			return {
				...step,
				boardReference: effectiveBoard,
				boardStageReference: effectiveStage,
				dealSize: effectiveUpdateExisting ? null : parseFloat(sanitizeCurrencyStringValue(effectiveDealSize)),
				updateExistingOpportunity: effectiveUpdateExisting,
				contactFilter: effectiveContactFilter ?? undefined,
				sendFromOptions: effectiveSendFrom,
			};
		},
		[selectedBoard, selectedStage, dealSize, updateExisting, baseState.skipStepContactFilter]
	);

	const { save } = useStepSave<Api.IOpportunityAutomationStep, UseOpportunityStepSaveOverrides>({
		step: opportunityStep,
		stepId,
		stepType: Api.AutomationStepType.Opportunity,
		templateId: template?.id,
		autoSave: baseState.autoSave,
		buildUpdatedStep,
	});

	return {
		// Opportunity-specific state
		selectedBoard,
		setSelectedBoard,
		selectedStage,
		setSelectedStage,
		dealSize,
		setDealSize,
		updateExisting,
		setUpdateExisting,
		boardOptions,
		stageOptions,
		isLoadingBoards: boardsQuery.isLoading,
		// Base state
		showRequiredFieldError: baseState.showRequiredFieldError,
		setShowRequiredFieldError: baseState.setShowRequiredFieldError,
		skipStepContactFilter: baseState.skipStepContactFilter,
		setSkipStepContactFilter: baseState.setSkipStepContactFilter,
		advancedOptionsToggled: baseState.advancedOptionsToggled,
		setAdvancedOptionsToggled: baseState.setAdvancedOptionsToggled,
		autoSave: baseState.autoSave,
		clearInlineErrorMessage: baseState.clearInlineErrorMessage,
		// Save action
		save,
	};
}
